import { useMemo, useState } from "react";
import {
  PoundSterling,
  AlertCircle,
  LoaderCircle,
  CircleCheckBig,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { z } from "zod";
import { TFunction } from "i18next";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { useNavigate } from "react-router";
import {
  CreateWithdrawErrorType,
  createWithdrawRequest,
} from "@/api/wallet.ts";
import { toast } from "sonner";
import { useUser } from "@/hooks/useUser.ts";
import { getErrorResponse } from "@/api/common/handleAxiosError.ts";
import { useTranslation } from "@/i18n";

const createFormSchema = (t: TFunction) =>
  z.object({
    amount: z
      .string()
      .transform((val) => (val ? parseFloat(val) : undefined))
      .refine((val) => val && !isNaN(val) && val >= 1.0, {
        message: t("validation.invalidAmount"),
      }),
    bankName: z.string().min(1, t("validation.bankNameRequired")),
    accountNumber: z.string().refine((number) => /^\d{8}$/.test(number), {
      message: t("validation.invalidAccountNumber"),
    }),
    sortCode: z
      .string()
      .refine((code) => /^(\d{2}-\d{2}-\d{2}|\d{6})$/.test(code), {
        message: t("validation.invalidSortCode"),
      }),
    city: z.string().min(1, { message: t("validation.invalidCity") }),
    county: z.string().min(1, { message: t("validation.invalidCounty") }),
    address1: z.string().min(3, { message: t("validation.invalidAddress") }),
    address2: z.string().optional(),
    postcode: z
      .string()
      .refine(
        (postcode) => /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/i.test(postcode),
        {
          message: t("validation.invalidPostcode"),
        },
      ),
    phoneNumber: z
      .string()
      .min(10, { message: t("validation.invalidPhoneNumber") }),
  });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const Withdraw = () => {
  const user = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [withdrawAll, setWithdrawAll] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formSchema = useMemo(() => createFormSchema(t), [t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      address1: user!.address1 || undefined,
      address2: user!.address2 || undefined,
      city: user!.city || undefined,
      county: user!.county || undefined,
      phoneNumber: user!.phone_number || undefined,
      postcode: user!.postcode || undefined,
    },
  });

  const onSubmit = async (values: FormValues) => {
    setLoading(true);

    try {
      await createWithdrawRequest({ ...values, amount: values.amount! });

      toast(t("withdraw.success"), {
        duration: 3000,
        icon: <CircleCheckBig className="size-4 text-green-600" />,
      });

      // Go back to wallet page after a short delay
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (e: unknown) {
      const response = getErrorResponse<CreateWithdrawErrorType>(e);
      setError(
        Object.values(response as CreateWithdrawErrorType)[0] ||
          t("withdraw.error"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawAllChange = (checked: boolean) => {
    setWithdrawAll(checked);
    if (checked) {
      form.setValue("amount", +user!.balance);
    } else {
      form.setValue("amount", undefined);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md min-w-[45vw] flex-col">
      <Card className="border-none shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl">{t("withdraw.heading")}</CardTitle>
          <CardDescription>{t("withdraw.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mx-auto my-4 w-fit">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("withdraw.failed")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="withdrawAll"
                    checked={withdrawAll}
                    onCheckedChange={handleWithdrawAllChange}
                  />
                  <Label
                    htmlFor="withdrawAll"
                    className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {t("withdraw.withdrawAll", { balance: user!.balance })}
                  </Label>
                </div>
              </div>

              {!withdrawAll && (
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.amount")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <PoundSterling className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-gray-500" />
                          <Input
                            id="amount"
                            type="string"
                            placeholder={t("form.amountPlaceholder")}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setError("");
                            }}
                            className="pl-10"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="bankName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("withdraw.bankName")}</FormLabel>
                    <FormControl>
                      <Input
                        id="bankName"
                        type="string"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="accountNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("withdraw.accountNumber")}</FormLabel>
                    <FormControl>
                      <Input
                        id="accountNumber"
                        type="string"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sortCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("withdraw.sortCode")}</FormLabel>
                    <FormControl>
                      <Input
                        id="sortCode"
                        type="string"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.city")}</FormLabel>
                    <FormControl>
                      <Input
                        id="city"
                        type="string"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="county"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.county")}</FormLabel>
                    <FormControl>
                      <Input
                        id="county"
                        type="string"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.address1")}</FormLabel>
                    <FormControl>
                      <Input
                        id="address1"
                        type="string"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.address2")}</FormLabel>
                    <FormControl>
                      <Input
                        id="address2"
                        type="string"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="postcode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.postcode")}</FormLabel>
                    <FormControl>
                      <Input
                        id="postcode"
                        type="string"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.phoneNumberLower")}</FormLabel>
                    <FormControl>
                      <Input
                        id="phoneNumber"
                        type="phone"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full py-6 text-lg"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <LoaderCircle className="animate-spin" />
                    {t("common.processing")}
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    {t("withdraw.submit")}
                  </div>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="text-muted-foreground text-center text-xs">
          {t("withdraw.processingNote")}
        </CardFooter>
      </Card>
    </div>
  );
};

export default Withdraw;
